import React from 'react';
import { BiCollapseVertical, BiExpandVertical } from 'react-icons/bi';

import { Select, Tooltip } from 'antd';
import PlanActualSegmented from 'components/common/PlanActualSegmented';
import {
  TASKS_GRID_COLLAPSE_ALL_LABEL,
  TASKS_GRID_EXPAND_ALL_LABEL,
  TASKS_GRID_FILTER_LABEL,
  TASKS_GRID_HEADING,
} from 'constants/uiConstants';
import { useElementWidth } from 'hooks/common/useElementWidth';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { pxToRem } from 'utils/cssUnits';
import MergeClassNames from 'utils/MergeClassNames';

import { STEP_CATEGORY_FILTERS } from './stepCategoryFilters';
import TasksViewOptions from './TasksViewOptions';
import type { TasksViewPreferences } from './tasksViewPreferences';

// Below this width (rem) the new layout's filter chips become a dropdown.
const NARROW_HEADER_BELOW_REM = 56;

interface InputTasksHeaderProps {
  activeStepCategoryKey: string;
  onStepCategoryChange: (categoryKey: string) => void;
  activeTaskCategory: InputTaskCategory;
  onTaskCategoryChange: (category: InputTaskCategory) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  viewPreferences: TasksViewPreferences;
  onViewPreferencesChange: (
    update: (previous: TasksViewPreferences) => TasksViewPreferences
  ) => void;
  onResetViewOptions: () => void;
}

const InputTasksHeader: React.FC<InputTasksHeaderProps> = ({
  activeStepCategoryKey,
  onStepCategoryChange,
  activeTaskCategory,
  onTaskCategoryChange,
  onExpandAll,
  onCollapseAll,
  viewPreferences,
  onViewPreferencesChange,
  onResetViewOptions,
}) => {
  const { ref: rowRef, width } = useElementWidth<HTMLDivElement>();
  // New layout only: on a narrow row the filter chips become one dropdown,
  // so the row needn't scroll sideways (OP-2556).
  const isNarrow =
    viewPreferences.sheetLayout &&
    width > 0 &&
    pxToRem(width) < NARROW_HEADER_BELOW_REM;

  return (
    <div className="mt-6 flex flex-col gap-4">
      <h2 className="text-lg text-gray-muted">{TASKS_GRID_HEADING}</h2>
      <div
        ref={rowRef}
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
      >
        <div className={isNarrow ? undefined : 'overflow-x-auto pb-1'}>
          <div className="flex w-max items-center gap-2">
            {isNarrow ? (
              <Select
                size="small"
                className="w-44"
                aria-label={TASKS_GRID_FILTER_LABEL}
                value={activeStepCategoryKey}
                options={STEP_CATEGORY_FILTERS.map((category) => ({
                  value: category.key,
                  label: category.label,
                }))}
                onChange={onStepCategoryChange}
              />
            ) : (
              STEP_CATEGORY_FILTERS.map((category) => {
                const isActive = category.key === activeStepCategoryKey;
                return (
                  <button
                    key={category.key}
                    type="button"
                    className={MergeClassNames(
                      'rounded-[var(--radius-button)] px-3 py-1.5 text-sm font-semibold',
                      isActive
                        ? 'bg-charcoal text-white'
                        : 'bg-transparent text-gray-dark hover:bg-rowHover'
                    )}
                    onClick={() => onStepCategoryChange(category.key)}
                  >
                    {category.label}
                  </button>
                );
              })
            )}
            <div className="mx-1 h-5 w-px bg-gray-medium" />
            <Tooltip title={TASKS_GRID_COLLAPSE_ALL_LABEL}>
              <button
                type="button"
                aria-label={TASKS_GRID_COLLAPSE_ALL_LABEL}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-rowHover active:bg-rowHover"
                onClick={onCollapseAll}
              >
                <BiCollapseVertical size={16} />
              </button>
            </Tooltip>
            <Tooltip title={TASKS_GRID_EXPAND_ALL_LABEL}>
              <button
                type="button"
                aria-label={TASKS_GRID_EXPAND_ALL_LABEL}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-rowHover active:bg-rowHover"
                onClick={onExpandAll}
              >
                <BiExpandVertical size={16} />
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <TasksViewOptions
            preferences={viewPreferences}
            onChange={onViewPreferencesChange}
            onReset={onResetViewOptions}
          />
          <PlanActualSegmented
            value={activeTaskCategory}
            onChange={onTaskCategoryChange}
          />
        </div>
      </div>
    </div>
  );
};

export default InputTasksHeader;
